import { Box, TextField } from '@material-ui/core';
import React from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';

type FormNumberFieldProps = {
  name: string;
  label?: string;
  rule?: RegisterOptions;
  required?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  readOnly?: boolean;
  variant?: 'standard' | 'outlined';
  defaultValue?: number | string;
  unit?: string;
  isFormatWithComma?: boolean;
  maxLength?: number;
  preventZero?: boolean;
};

export const NumberFieldController: React.FC<FormNumberFieldProps> = ({
  variant = 'outlined',
  defaultValue = '',
  isFormatWithComma,
  ...props
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<any>();
  const error = props.name
    .replace(/\[(\d+)\]/g, '.$1') // products[0] -> products.0
    .split('.')
    .filter(Boolean)
    .reduce((acc, part) => acc && acc[part], errors);

  /** Format number -> 1,000 */
  const formatNumber = (value: string | number): string => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(num)) return '';
    return isFormatWithComma ? num.toLocaleString('en-US') : String(num);
  };

  /** keep only number */
  const normalizeNumber = (value: string): string => {
    if (!value) return '';
    return value.replace(/[^\d]/g, '');
  };

  return (
    <Controller
      name={props.name}
      control={control}
      defaultValue={defaultValue}
      rules={props.rule}
      render={({ field }) => {
        const { ref, value, onChange, ...remain } = field;

        return (
          <Box display="flex" alignItems="center">
            <TextField
              {...props}
              {...remain}
              fullWidth
              margin="normal"
              variant={variant}
              error={!!error}
              helperText={error?.message ?? ''}
              value={formatNumber(value)}
              onChange={(e) => {
                const raw = normalizeNumber(e.target.value); // remove commas

                // If preventZero = true and value is '0' or empty after typing '0', prevent update
                if (props.preventZero && (raw === '0' || (raw === '' && value === '0'))) {
                  return;
                }

                onChange(raw); // update RHF with pure number string
              }}
              InputProps={{
                readOnly: props.readOnly,
                inputRef: ref,
              }}
              inputProps={{
                maxLength: props.maxLength,
              }}
            />
            {props.unit && (
              <Box component="span" ml={1} mt={2} fontSize={16} fontWeight={600}>
                {props.unit}
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
};
